/**
 * MSG91 web OTP widget — typed wrapper around `window.initSendOTP`.
 *
 * Why MSG91 on web (and not Firebase Phone Auth):
 *   - The mobile app already uses MSG91 (`@msg91comm/sendotp-react-native`)
 *     so customers receive OTPs from the same sender id whether they
 *     sign in via mobile or web — better trust + brand consistency.
 *   - Firebase Phone Auth on web is rate-limited to a small free SMS
 *     quota per day; MSG91 has a contractual volume + better
 *     deliverability across Indian carriers.
 *   - The server-side `mintCustomToken` callable already validates
 *     MSG91 access tokens (`functions/src/auth.ts`) — web reuses
 *     that endpoint instead of needing a separate web-only path.
 *
 * Flow:
 *   1. `loadWidget()` ensures `window.initSendOTP` exists (the
 *      script tag in `index.html` loads it).
 *   2. `openMsg91OtpModal({ widgetId, tokenAuth })` opens MSG91's
 *      hosted modal; user enters phone + OTP entirely inside it.
 *   3. On success the modal returns an access-token JWT we send to
 *      `mintCustomToken` for the Firebase auth handshake.
 *
 * The widget renders its own UI — we don't build a phone-input
 * page on our side anymore. Cleaner UX + one less surface to maintain.
 */

/** Shape of the success payload MSG91's widget returns. Field names
 *  shift slightly across MSG91 widget versions — `access-token` AND
 *  `message` may both contain the JWT (the JWT itself is sometimes
 *  in `message` when the widget is configured a certain way). We
 *  normalise in `extractAccessToken()`. */
type Msg91SuccessPayload = {
  type?: 'success' | 'error';
  message?: string;
  ['access-token']?: string;
  /** Verified phone in E.164 (with leading +) or raw 10-digit;
   *  MSG91 sometimes returns it without the country code prefix. */
  identifier?: string;
  /** Country-code prefix (e.g. '91' for India). When present, we
   *  use it to construct the full E.164 phone. */
  countryCode?: string;
  invisibleVerified?: boolean;
};

type Msg91FailurePayload = {
  type?: 'error';
  message?: string;
  code?: number | string;
};

type Msg91WidgetConfig = {
  /** MSG91 widget id — get from MSG91 dashboard → OTP widget. The
   *  WEB widget id; do NOT reuse the mobile widget id (they have
   *  different "integration type" requirements). */
  widgetId: string;
  /** MSG91 token auth — public widget identifier paired with the
   *  widget id. NOT a secret (the real secret is `MSG91_AUTH_KEY`
   *  on the server, used to verify the access-token MSG91 returns
   *  to us). */
  tokenAuth: string;
  /** Don't expose `sendOtp` / `verifyOtp` on window — the widget
   *  handles the entire UX in its own modal. */
  exposeMethods: false;
  /** Optional pre-fill — when the customer has previously signed
   *  in we could remember their phone and skip typing it. Out of
   *  scope for v1. */
  identifier?: string;
  success: (data: Msg91SuccessPayload) => void;
  failure: (error: Msg91FailurePayload) => void;
};

declare global {
  interface Window {
    /** MSG91 widget global. Set by their hosted script in index.html. */
    initSendOTP?: (config: Msg91WidgetConfig) => void;
  }
}

/** CDN URLs for MSG91's OTP provider — primary + secondary. MSG91's
 *  main CDN (verify.msg91.com) is occasionally blocked on enterprise
 *  networks; their secondary host (verify.phone91.com) serves the
 *  identical script. We try primary first, fall back to secondary on
 *  script-load error. */
const MSG91_SCRIPT_URLS = [
  'https://verify.msg91.com/otp-provider.js',
  'https://verify.phone91.com/otp-provider.js',
];

/** Promise-cached script-injection. The MSG91 provider script is
 *  loaded lazily ON FIRST CALL — NOT eagerly in `index.html` — for
 *  two reasons:
 *    1. Performance: signed-in users (the common case) never need
 *       the OTP script and shouldn't pay for its download.
 *    2. Console hygiene: the MSG91 script attaches global DOM hooks
 *       that throw `Cannot read properties of null (reading 'value')`
 *       on pages without an OTP widget. React's error-boundary
 *       attribution mis-blames those async throws on whichever
 *       component is in the tree when they fire (e.g. OverviewTab).
 *       Loading only at sign-in time keeps the noise contained.
 *
 *  Cached as a module-scope promise so concurrent callers share one
 *  injection. */
let widgetLoadPromise: Promise<void> | null = null;

function injectScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = url;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${url}`));
    document.head.appendChild(s);
  });
}

async function loadWidget(): Promise<void> {
  if (typeof window === 'undefined') {
    throw new Error('MSG91 widget cannot run outside a browser context.');
  }
  if (window.initSendOTP) return;
  if (widgetLoadPromise) return widgetLoadPromise;

  widgetLoadPromise = (async () => {
    // Try each CDN URL in order; fall back to the next on failure.
    let lastErr: Error | null = null;
    for (const url of MSG91_SCRIPT_URLS) {
      try {
        await injectScript(url);
        break;
      } catch (e) {
        lastErr = e as Error;
      }
    }

    // Even after the script tag fires `onload`, `window.initSendOTP`
    // can take a beat to bind. Poll briefly for it (matches the old
    // behaviour from when the script was eager-loaded in index.html).
    const start = Date.now();
    const timeoutMs = 2_000;
    while (!window.initSendOTP && Date.now() - start < timeoutMs) {
      await new Promise((r) => setTimeout(r, 50));
    }
    if (!window.initSendOTP) {
      // Reset the cache so a later sign-in attempt retries instead of
      // resolving against a stale failed load.
      widgetLoadPromise = null;
      throw (
        lastErr ??
        new Error(
          'OTP service did not load. Check your internet connection and try again.',
        )
      );
    }
  })();

  return widgetLoadPromise;
}

export type Msg91OtpResult = {
  /** Verified access-token JWT — send to `mintCustomToken` which
   *  will call MSG91's `verifyAccessToken` server-side to resolve
   *  the phone number. */
  accessToken: string;
  /** Full E.164 phone (with leading +) if MSG91's web widget gave
   *  it to us — usually it does NOT (the web widget keeps the phone
   *  server-side; only the server's `verifyAccessToken` call can
   *  resolve it). Always present when called from mobile. Empty
   *  string when the web widget didn't expose it — `mintCustomToken`
   *  handles the resolution on its own. */
  phoneE164: string;
};

/** Open the MSG91 OTP modal and resolve once the user has completed
 *  verification (entered phone, received SMS, entered code). Rejects
 *  on widget error, user dismissal, or timeout.
 *
 *  WIDGET LIFECYCLE NOTE: MSG91's widget is stateful — calling
 *  `initSendOTP` a second time without a full page reload sometimes
 *  shows the previous session's state. We work around this by
 *  expecting the caller to only invoke this once per sign-in attempt
 *  and letting the widget's own UX handle retries. */
export function openMsg91OtpModal(args: {
  widgetId: string;
  tokenAuth: string;
  prefilledPhone?: string;
}): Promise<Msg91OtpResult> {
  return new Promise((resolve, reject) => {
    void loadWidget().then(() => {
      try {
        window.initSendOTP!({
          widgetId: args.widgetId,
          tokenAuth: args.tokenAuth,
          exposeMethods: false,
          identifier: args.prefilledPhone,
          success: (data) => {
            // Log the raw payload so anyone debugging a sign-in
            // failure can inspect what MSG91 actually returned.
            // The shape varies by widget config + MSG91 widget
            // version, so we capture for triage. No PII concern
            // (this is in the user's own browser console).
            console.info('[msg91] widget success payload', data);

            const token = extractAccessToken(data);
            if (!token) {
              reject(new Error('OTP verified but no access token received. Please try again.'));
              return;
            }
            // Phone is BEST-EFFORT on web. MSG91's web widget
            // commonly returns just `{ type: 'success', message: <JWT> }`
            // — no phone at all. That's OK: `mintCustomToken`
            // calls `verifyAccessToken` server-side and resolves
            // the phone from MSG91's authenticated response (which
            // is the authoritative source anyway).
            const phone =
              extractPhoneE164(data, token, args.prefilledPhone) ?? '';
            resolve({ accessToken: token, phoneE164: phone });
          },
          failure: (err) => {
            reject(new Error(err.message ?? 'OTP verification failed.'));
          },
        });
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    }, reject);
  });
}

/** MSG91 widget returns the JWT in either `access-token` or
 *  `message` depending on widget configuration. Handle both. */
function extractAccessToken(data: Msg91SuccessPayload): string | null {
  const fromHeader = data['access-token']?.trim();
  if (fromHeader) return fromHeader;
  // Some widget configs put the JWT in `message`. JWTs start with
  // "eyJ" (base64 of `{"`) and have exactly 3 dot-separated parts.
  const msg = data.message?.trim();
  if (msg && msg.startsWith('eyJ') && msg.split('.').length === 3) return msg;
  return null;
}

/** Compose E.164 from MSG91's response.
 *
 *  STRATEGY (in order — first hit wins):
 *    1. Decode the access-token JWT and read the verified mobile
 *       from its payload. This is the MOST RELIABLE source because
 *       it's the value MSG91 actually delivered the OTP to and
 *       therefore the value `verifyAccessToken` will match against
 *       server-side. Field name varies (`mobile` / `phoneNumber` /
 *       `phone`) by MSG91 widget version.
 *    2. Use `data.identifier` (+ optional `countryCode`) from the
 *       widget callback. Works on older widget builds.
 *    3. Fall back to the prefilled phone the caller passed in.
 *
 *  If all three fail, returns null and the caller surfaces a
 *  "phone not returned" error to the user. */
function extractPhoneE164(
  data: Msg91SuccessPayload,
  accessToken: string,
  prefilledPhone: string | undefined,
): string | null {
  // ── 1. JWT-payload extraction (most reliable) ─────────────────
  const jwtPhone = phoneFromJwt(accessToken);
  if (jwtPhone) return jwtPhone;

  // ── 2. Widget callback fields ─────────────────────────────────
  const id = data.identifier?.trim();
  // 2a. Identifier WITH leading + → use as-is.
  if (id && id.startsWith('+')) return id;
  // 2b. Identifier (raw digits) + countryCode → join.
  if (id && data.countryCode) {
    return `+${data.countryCode}${id.replace(/^\+/, '')}`;
  }
  // 2c. Just identifier without country code → assume India (+91).
  //     Reasonable default given our customer base.
  if (id && /^\d{10}$/.test(id)) {
    return `+91${id}`;
  }
  // 2d. Identifier is 12 digits starting with 91 (India without +).
  if (id && /^91\d{10}$/.test(id)) {
    return `+${id}`;
  }

  // ── 3. Prefilled phone (last resort) ──────────────────────────
  if (prefilledPhone) {
    return prefilledPhone.startsWith('+') ? prefilledPhone : `+${prefilledPhone}`;
  }
  return null;
}

/** Decode the access-token JWT's payload and pull the phone out.
 *  No signature verification on the client — the server's call to
 *  MSG91's `verifyAccessToken` is the real authority. Here we're
 *  just reading a field MSG91 already wrote. */
function phoneFromJwt(jwt: string): string | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 (replace URL-safe chars, pad to multiple of 4)
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice(0, (4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json) as Record<string, unknown>;
    // MSG91's JWT field naming has varied — check the few names
    // they've shipped in different widget versions.
    const candidate =
      pickString(payload.mobile)
      ?? pickString(payload.phoneNumber)
      ?? pickString(payload.phone)
      ?? pickString(payload.identifier);
    if (!candidate) return null;
    const digits = candidate.replace(/\D/g, '');
    // 10 digits → assume India (no country code in token, which
    // happens when the widget is single-country-configured).
    if (/^\d{10}$/.test(digits)) return `+91${digits}`;
    // 12 digits starting with 91 → India E.164 minus the +.
    if (/^91\d{10}$/.test(digits)) return `+${digits}`;
    // Anything else longer than 10 → trust it as E.164 minus +.
    if (digits.length > 10) return `+${digits}`;
    return null;
  } catch {
    return null;
  }
}

function pickString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}
